import React, { useCallback, useState } from 'react'
import { marginSizes } from '@jarvis.oddle/core/theme/default'
import {
  OrderItemContainer,
  OrderItemDetails,
  OrderItemImage
} from '@jarvis.oddle/core/molecules/OrderItem/OrderItem'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import DivButton from '@jarvis.oddle/core/components/DivButton'
import QuantitySelector from '@jarvis.oddle/core/components/QuantitySelector'
import { P } from '@jarvis.oddle/core/components/Typo'
import { useProductImageState } from 'src/components/molecules/ProductImageState'
import { updateStoreOrderItemQuantityAction } from 'src/redux/order/action'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { useAppContext } from 'src/containers/AppContext'
import { debounce } from 'lodash'
import IconDelete from '@jarvis.oddle/core/components/Icons/IconDelete'

const EditableOrderItem = ({
  item
}: {
  item: Oms.V4.OrderItem
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const { getProductImagePath, formatMoney } = useAppContext()
  const { showImage, showDefaultImage } = useProductImageState()
  const [tempQuantity, setTempQuantity] = useState(item.quantity)

  const onQuantityChange = async (_, quantity) => {
    await dispatch(
      updateStoreOrderItemQuantityAction({ orderItemId: item.id, quantity })
    )
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(debounce(onQuantityChange, 500), [])

  const onItemDelete = async () => {
    await dispatch(
      updateStoreOrderItemQuantityAction({ orderItemId: item.id, quantity: 0 })
    )
  }

  const {
    details: {
      imageFullUrls: [imageFullUrl] = [],
      imageUrls: [imageUrl] = []
    } = {}
  } = item
  const imageSrc = getProductImagePath(
    imageFullUrl || imageUrl,
    showDefaultImage,
    {
      shouldCache: true,
      containerWidth: 120,
      containerHeight: 120
    }
  )

  const choicesParser = (item) => item.subitemChoices

  return (
    <Div margin={marginSizes.extraSmall} marignTop={marginSizes.small}>
      <OrderItemContainer>
        <Flex width='100%' flexDirection='row'>
          {showImage && imageSrc && (
            <Flex
              flexDirection='column'
              justifyContent='flex-start'
              marginRight={marginSizes.small}
            >
              <OrderItemImage src={imageSrc} />
            </Flex>
          )}
          <OrderItemDetails>
            <Flex
              width='100%'
              flexDirection='row'
              justifyContent='space-between'
              alignItems='flex-start'
            >
              <Div>
                <P bold margin={marginSizes.none}>
                  {item.name}
                </P>
                {(choicesParser(item) || []).map((c) => (
                  <P normalised key={c.id}>{`${c.quantity}x ${c.name}`}</P>
                ))}
              </Div>
              <DivButton
                data-testid='delete'
                onClick={onItemDelete}
                width='20px'
                height='20px'
              >
                <IconDelete />
              </DivButton>
            </Flex>
            <Flex
              width='100%'
              flexDirection='row'
              justifyContent='space-between'
              alignItems='center'
            >
              <QuantitySelector
                controlled
                name={item.id}
                withInput
                value={tempQuantity}
                onChange={(_, quantity) => {
                  setTempQuantity(quantity)
                  debounced(_, quantity)
                }}
              />
              <P>{formatMoney(item.quantity * item.price)}</P>
            </Flex>
          </OrderItemDetails>
        </Flex>
      </OrderItemContainer>
    </Div>
  )
}

export default EditableOrderItem
